/* @flow */

import {
  requestGetProducts,
  requestGetProduct,
  requestGetProductComments,
  processSnapshotsArr,
  requestGetTags,
  requestGetTypes,
  requestGetBrands,
  requestGetSuppliers,
  requestAddProductComment,
  requestAddProductToFavorites,
  requestRemoveProductFromFavorites,
  requestGetUserFavorites,
  requestDeleteProductComment,
  requestAddOrder,
  requestGetUserOrders,
  requestGetOrders,
  requestUpdateOrder,
  requestUpdateStateOrder,
  subscribeToOrderChanges,
  requestGetProductsByList,
  requestGetOrder,
} from './client';

import c from '../constants';

let cacheData = {};

export function loadCacheData() {
  const aux = localStorage.getItem('cacheData');
  if (aux) {
    cacheData = { ...JSON.parse(aux), ...cacheData };
  }
}
export function saveCacheData(obj: Object) {
  cacheData = { ...cacheData, ...obj };
  localStorage.setItem('cacheData', JSON.stringify(cacheData));
}
export function getCacheData(): Object {
  return cacheData;
}

export function getTagsList(): Function {
  return (dispatch: Function, getState: Function): Promise<any> => {
    const currUser = getState().account?.user;
    if (
      !currUser?.admin &&
      cacheData.tags &&
      cacheData.tagsDate > new Date().getTime() - 1000 * 60 * 2 // 2 minutes
    ) {
      return Promise.resolve(cacheData.tags);
    }
    return requestGetTags().then((querySnapshot) => {
      const tags = processSnapshotsArr(querySnapshot).map((obj: Object) => {
        return {
          value: obj.id,
          label: obj.data.name || obj.id,
          count: obj.data.count,
          list: obj.data.list,
        };
      });
      saveCacheData({
        tags,
        tagsDate: new Date().getTime(),
      });
      return tags;
    });
  };
}

export function getTypesList(forceLoad: boolean = false): Function {
  return (dispatch: Function, getState: Function): Promise<any> => {
    const currUser = getState().account?.user;
    if (
      !currUser?.admin &&
      !forceLoad &&
      cacheData.productTypes &&
      cacheData.productTypesDate > new Date().getTime() - 1000 * 60 * 2 // 2 minutes
    ) {
      return Promise.resolve(cacheData.productTypes);
    }
    return requestGetTypes().then((querySnapshot) => {
      const productTypes = processSnapshotsArr(querySnapshot).map(
        (obj: Object) => {
          return {
            value: obj.id,
            label: obj.data.name || obj.id,
            count: obj.data.count,
            list: obj.data.list,
          };
        },
      );
      saveCacheData({
        productTypes,
        productTypesDate: new Date().getTime(),
      });
      return productTypes;
    });
  };
}

export function getBrandsList(): Function {
  return (dispatch: Function, getState: Function): Promise<any> => {
    const currUser = getState().account?.user;
    if (
      !currUser?.admin &&
      cacheData.productBrands &&
      cacheData.productBrandsDate > new Date().getTime() - 1000 * 60 * 2 // 2 minutes
    ) {
      return Promise.resolve(cacheData.productBrands);
    }
    return requestGetBrands().then((querySnapshot) => {
      const productBrands = processSnapshotsArr(querySnapshot).map(
        (obj: Object) => {
          return {
            value: obj.id,
            label: obj.data.name || obj.id,
            count: obj.data.count,
            list: obj.data.list,
          };
        },
      );
      saveCacheData({
        productBrands,
        productBrandsDate: new Date().getTime(),
      });
      return productBrands;
    });
  };
}

export function getSuppliersList(): Function {
  return (dispatch: Function, getState: Function): Promise<any> => {
    const currUser = getState().account?.user;
    if (
      !currUser?.admin &&
      cacheData.productSuppliers &&
      cacheData.productSuppliersDate > new Date().getTime() - 1000 * 60 * 2 // 2 minutes
    ) {
      return Promise.resolve(cacheData.productSuppliers);
    }
    return requestGetSuppliers().then((querySnapshot) => {
      const productSuppliers = processSnapshotsArr(querySnapshot).map(
        (obj: Object) => {
          return {
            value: obj.id,
            label: obj.data.name || obj.id,
            count: obj.data.count,
            list: obj.data.list,
          };
        },
      );
      saveCacheData({
        productSuppliers,
        productSuppliersDate: new Date().getTime(),
      });
      return productSuppliers;
    });
  };
}

let lastQuery = {};
export function getProductsList(
  props: Object = {},
  startAfterDate: any = null,
): Function {
  let params: Object = {};
  if (props.type != null) params.type = props.type;
  if (props.barcode != null && props.barcode.length > 0)
    params.barcode = props.barcode;
  if (props.sku != null && props.sku.length > 0) params.sku = props.sku;
  if (props.tags != null) params.tags = props.tags;
  if (props.branch != null) params.branch = props.branch;
  if (props.points != null) params.points = props.points;
  if (props.orderByPoints != null) params.orderByPoints = props.orderByPoints;
  if (props.minPoints != null) params.minPoints = props.minPoints;
  if (props.maxPoints != null) params.maxPoints = props.maxPoints;
  return (dispatch: Function) => {
    params.limit = props.smallLimit ? 10 : 25;
    return requestGetProducts(params, startAfterDate).then(
      (querySnapshot): Array<Object> => {
        let newProducts = processSnapshotsArr(querySnapshot).map(
          (obj: Object) => {
            return obj;
          },
        );

        const parseProductPoints = (product) => {
          const rawPoints = product?.data?.points;
          const parsed = Number(rawPoints);
          return Number.isFinite(parsed) ? parsed : null;
        };

        if (params.minPoints != null || params.maxPoints != null) {
          const min =
            params.minPoints != null ? Number(params.minPoints) : null;
          const max =
            params.maxPoints != null ? Number(params.maxPoints) : null;
          newProducts = newProducts.filter((product) => {
            const points = parseProductPoints(product);
            if (points == null) return false;
            if (min != null && points < min) return false;
            if (max != null && points > max) return false;
            return true;
          });
        }

        if (params.orderByPoints) {
          newProducts = newProducts.sort((a, b) => {
            const pointsA = parseProductPoints(a);
            const pointsB = parseProductPoints(b);
            if (pointsA == null && pointsB == null) return 0;
            if (pointsA == null) return 1;
            if (pointsB == null) return -1;
            return pointsA - pointsB;
          });
        }

        dispatch({
          type: c.UPDATE_PRODUCTS,
          payload: newProducts,
          add: startAfterDate != null,
        });
        const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
        if (querySnapshot.docs.length < params.limit) {
          return null;
        }
        return lastVisible;
      },
    );
  };
}

export function getProductsRecommendations(props: string): any {
  let startAfterDate = null;
  return (dispatch: Function) => {
    const params = {
      limit: 5,
    };
    if (props.tags != null) params.tags = props.tags;
    return requestGetProducts(params, startAfterDate).then(
      (querySnapshot): Array<Object> => {
        const newProducts = processSnapshotsArr(querySnapshot).map(
          (obj: Object) => {
            return obj;
          },
        );
        return newProducts;
      },
    );
  };
}

export function getProduct(productId: Object): Function {
  return (dispatch: Function) => {
    return requestGetProduct(productId).then((doc) => {
      if (doc.exists) {
        const res = doc.data();
        dispatch({
          type: c.UPDATE_PRODUCT,
          payload: {
            ...res,
            id: productId,
          },
        });
        return res;
      }
      return { error: true };
    });
  };
}

export function getProductsComments(productId: Object): Function {
  return (dispatch: Function) => {
    const updateComments = (comments: Array<Object>) => {
      dispatch({
        type: c.UPDATE_COMMENTS,
        payload: {
          id: productId,
          comments: comments.map((o) => ({
            ...o,
            data: {
              ...o.data,
              timestamp: o.data.timestamp.toDate(),
            },
          })),
        },
      });
    };
    return requestGetProductComments(productId, {
      subscribe: false,
    }).then((comments) => {
      updateComments(comments);
      return comments;
    });
  };
}

export function addProductComment(
  productId: Object,
  comment: Object,
  rating: Object,
): Function {
  return (dispatch: Function) => {
    return requestAddProductComment(productId, comment, rating).then((res) => {
      dispatch({
        type: c.UPDATE_COMMENTS,
        payload: {
          id: productId,
          comments: [res],
        },
      });
      return res;
    });
  };
}

export function deleteProductComment(productId, commentId) {
  return (dispatch) => {
    return requestDeleteProductComment(productId, commentId).then((res) => {
      dispatch({
        type: c.DELETE_COMMENT,
        payload: {
          id: productId,
          commentId: commentId,
        },
      });
      return res;
    });
  };
}

export function toggleProductFromFavorite(
  product: Object,
  isFavorite: boolean,
) {
  return (dispatch, getState) => {
    let toggleRequest;
    if (isFavorite) {
      toggleRequest = requestRemoveProductFromFavorites(product.id);
    } else {
      toggleRequest = requestAddProductToFavorites(product.id);
    }

    return toggleRequest
      .then(() => {
        if (isFavorite) {
          dispatch({
            type: c.REMOVE_PRODUCT_TO_FAVORITES,
            payload: product,
          });
        } else {
          dispatch({
            type: c.ADD_PRODUCT_TO_FAVORITES,
            payload: product,
          });
        }
      })
      .catch((error) => {
        console.error('Error toggling favorite:', error);
        throw error;
      });
  };
}

export function getFavoritesList() {
  return async (dispatch) => {
    try {
      const favorites = await requestGetUserFavorites();
      dispatch({
        type: c.UPDATE_FAVORITES,
        payload: favorites,
      });
      return favorites;
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };
}

export function addOrder(order) {
  return (dispatch) => {
    return requestAddOrder(order)
      .then((res) => {
        dispatch({
          type: c.ADD_ORDER,
          payload: res,
        });
        dispatch({
          type: c.CLEAR_CART,
        });
      })
      .catch((error) => {
        console.error('Error adding order:', error);
      });
  };
}

export function getUserOrdersList(): Function {
  return async (dispatch) => {
    try {
      const orders = await requestGetUserOrders();
      dispatch({
        type: c.UPDATE_ORDERS,
        payload: orders,
      });
      return orders;
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };
}

export const handleOrderChanges = (callback) => {
  return (dispatch, getState) => {
    const handleChange = async (orderData) => {
      const { type, orderId, data } = orderData;
      let message = '';
      // obtener el estado actual en cada cambio para evitar usar una copia stale
      const { orders } = getState().products;
      const orderExists = orders.some((order) => order.id === orderId);

      if (type === 'added') {
        // Si la orden trae una lista de productos, pre-cargar los productos faltantes
        try {
          const productList = data && data.products ? data.products : [];
          const productIds = productList.map((p) => p.id).filter(Boolean);
          if (productIds.length > 0) {
            const stateProducts = getState().products.list || [];
            const missing = productIds.filter(
              (pid) => !stateProducts.some((p) => p.id === pid),
            );
            if (missing.length > 0) {
              // esperar a que los productos se carguen antes de despachar la orden
              // esto hace que al darle "Verificar disponibilidad" el admin no espere a que los productos se traigan
              // desde el servidor en el momento
              // dispatch devuelve una promesa porque getProductsByList es async
              // eslint-disable-next-line no-await-in-loop
              await dispatch(getProductsByList(missing));
            }
          }
        } catch (err) {
          console.warn('Error preloading products for order change:', err);
        }
        switch (data.state) {
          case 'En cola': {
            callback({ message: `Orden en cola`, orderData });
            break;
          }
          case 'Confirmado': {
            callback({ message: `Se confirmó la orden`, orderData });
            break;
          }
          case 'Finalizado':
            break;
          case 'Cancelado':
            break;
          case 'No disponible':
            break;
          default:
            console.warn('Not Registered', data);
            break;
        }

        if (orderExists) {
          dispatch({
            type: 'UPDATE_ORDER',
            payload: { id: orderId, data },
          });
        } else {
          dispatch({
            type: 'ADD_ORDER',
            payload: { id: orderId, data },
          });
        }
      }
    };

    const unsubscribe = subscribeToOrderChanges(handleChange);

    return unsubscribe;
  };
};

export function getOrdersList(cb): Function {
  return async (dispatch) => {
    try {
      const orders = await requestGetOrders();
      dispatch({
        type: c.UPDATE_ORDERS,
        payload: orders,
      });

      dispatch(
        handleOrderChanges(({ message, orderData }) => {
          cb(message, orderData);
        }),
      );

      return orders;
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };
}

export function changeOrder(updatedOrder, orderId) {
  return async (dispatch) => {
    try {
      const { id, data } = await requestUpdateOrder(updatedOrder, orderId);
      dispatch({
        type: c.UPDATE_ORDER,
        payload: { id, data },
      });
      return data;
    } catch (error) {
      console.error('Error updating order:', error);
      throw error;
    }
  };
}

export function changeStateOrder(orderId, newState) {
  return async (dispatch) => {
    try {
      const { id, data } = await requestUpdateStateOrder(orderId, newState);
      dispatch({
        type: c.UPDATE_ORDER,
        payload: { id, data },
      });
      return data;
    } catch (error) {
      console.error('Error updating order:', error);
      throw error;
    }
  };
}

export function getOrder(orderId) {
  return async (dispatch) => {
    try {
      const order = await requestGetOrder(orderId);
      dispatch({
        type: c.ADD_ORDER,
        payload: {
          id: orderId,
          data: order.data,
        },
      });
      const productList = order.data.products || [];
      const productIds = productList.map((product) => product.id);

      if (productIds.length > 0) {
        await dispatch(getProductsByList(productIds));
      }

      return order;
    } catch (error) {
      console.error('Error al obtener y procesar el pedido:', error);
      return { success: false, error };
    }
  };
}

export function getProductsByList(productList) {
  return async (dispatch, getState) => {
    try {
      if (productList.length === 0) {
        dispatch({
          type: c.UPDATE_PRODUCTS,
          payload: [],
        });
        return [];
      }
      const productsData = await requestGetProductsByList(productList);
      const state = getState();
      const currentProducts = state.products.list || [];
      const updatedProducts = [
        ...currentProducts,
        ...productsData.filter(
          (newProduct) =>
            !currentProducts.some(
              (existingProduct) => existingProduct.id === newProduct.id,
            ),
        ),
      ];
      // Merge fetched products into existing products list to avoid replacing
      // the whole products list (which could remove previously loaded product data
      // and cause the UI to temporarily lose product details).
      dispatch({
        type: c.UPDATE_PRODUCTS,
        payload: updatedProducts,
      });

      return productsData;
    } catch (error) {
      console.error('Error getting products:', error);
      throw error;
    }
  };
}
